import { useEffect } from 'react'
import { reaction } from 'mobx'
import { shopStore, defaultShopStore, storeManager } from '@Stores/MainStore'
import { isNothing } from '@Utils/Utils'
import { logn, loge } from '@Utils/PikaLog'
import { findString } from '@Utils/String'
import { storeProduct } from '@Stores/StoreProduct'
import StoreAuth from '@Stores/User/StoreAuth'
import StoreUser from '@Stores/User/StoreUser'
import { Country } from '@Configs/Country'
import Raven from '@Raven'
import { useFirebaseCart } from '@Firebase/FirebaseCart'
import { StorageExtend } from '@Utils/StorageManager'

const localKeyPrefix = 'product-warehouse-selected'
const genLocalWarehouseKey = () => `${localKeyPrefix}`
const genRemoteWarehouseKey = ({ country_code, baId }) => `${localKeyPrefix}-${country_code}-${baId}`

const getRemoteWarehouseValue = async ({ country_code, baId }) => {
    let warehouseValue = null
    try {
        const warehouseKey = genRemoteWarehouseKey({ country_code, baId })
        const res = await Raven.getRemoteWarehouseSelected({ warehouseKey })
        const { value: data } = res
        warehouseValue = data && data.value ? data.value : null
    } catch (err) {
        console.error(err)
    }
    return warehouseValue
}

const _getSelectedWarehouse = ({ country_code, baId }) => {
    const warehouseKey = baId ? genRemoteWarehouseKey({ country_code, baId }) : genLocalWarehouseKey()

    return StorageExtend.getFromCountry(warehouseKey)
}

const _setSelectedWarehouse = ({ country_code, baId, warehouse }) => {
    const warehouseKey = baId ? genRemoteWarehouseKey({ country_code, baId }) : genLocalWarehouseKey()

    const storage = StorageExtend.getFromCountryAll()
    Object.keys(storage)
        .filter(key => new RegExp(`^${localKeyPrefix}`).test(key))
        .forEach(key => StorageExtend.unsetFromCountry(key)) // Remove exists keys

    StorageExtend.setToCountry(warehouse, warehouseKey)
    storeProduct.warehouseSelectedValue = warehouse

    if (baId) Raven.setRemoteWarehouseSelected({ warehouseKey, value: warehouse }) // Set into remote storage if login
}

const removeNotExistsCartItems = async () => {
    const remoteCart = useFirebaseCart()
    const { shop } = storeProduct
    const cartItems = await remoteCart.getCurrentCartItems()

    const listOfShopItemCode = shop.map(r => r.item_code)
    const listOfCartItemCode = cartItems.map(r => r.itemCode)

    const notExitsItemCode = listOfCartItemCode.filter(itemCode => !listOfShopItemCode.includes(itemCode))
    notExitsItemCode.forEach(itemCode => remoteCart.removeItem(itemCode))
}

const removeOutOfStockCartItems = async () => {
    const remoteCart = useFirebaseCart()
    const { shop } = storeProduct

    const listOfOutOfStockItemCode = shop.filter(r => r.qty_sellable < 1).map(r => r.item_code)

    listOfOutOfStockItemCode.forEach(itemCode => remoteCart.removeItem(itemCode))
}

const mappingToLegacyResponse = response => {
    const { items = [] } = response

    const categories = items.reduce((list, r) => [...list, ...r.categories], []).reduce((list, c) => (list.findIndex(({ id }) => id === c.id) > -1 ? list : [...list, c]), [])

    const legacyResponse = {
        status: 'success',
        message: '',
        data: items,
        categories,
        starterKits: [],
        hot: [],
        renewal: [],
        top10: [],
        trend_price: []
    }

    return legacyResponse
}

const processProductSource = response => {
    const { categories, top10, hot, data } = response

    if (hot !== undefined) {
        shopStore.hotProduct = hot
    }

    const productData = data

    shopStore.top10 = top10

    productData.map(item => {
        storeProduct.setProduct(item.item_code, item)
    })

    storeProduct.categories = categories
    storeProduct.shop = data

    logn('📦 Products data has been completed generate.')

    // * mark as done
    storeProduct.isInitialized = true
    // * support legacy product source code
    setShoppingProductSource(data)
}

const startProductAPI = () => {
    const status = StoreUser.getShortStatus()
    const baId = StoreAuth.id
    const allow = 'shop'
    const country_code = Country.getCode3()

    Raven.getWarehousesItems()
        .then(items => (storeProduct.warehouseItems = items))
        .then(async () => {
            const remoteKey = baId ? await getRemoteWarehouseValue({ country_code, baId }) : 'Main'

            let warehouse = _getSelectedWarehouse({ country_code, baId }) || remoteKey || 'Main'
            if (country_code === 'IDN') warehouse = 'Surabaya'
            _setSelectedWarehouse({ country_code, warehouse, baId })
        })
        .then(() =>
            Raven.getProductSourceV2({
                allow,
                status,
                warehouse: storeProduct.warehouseSelectedValue,
                ...(baId ? { baId } : {})
            })
        )
        .then(response => {
            const legacyResponse = mappingToLegacyResponse(response)
            processProductSource(legacyResponse)
            logn('✅ 🐉 Successfully load product (v2) source from database.')
        })
        .then(() => StoreAuth.isAuthorized && removeNotExistsCartItems())
        .then(() => StoreAuth.isAuthorized && removeOutOfStockCartItems())
        .catch(error => {
            loge('❌ 🐉 Failed to connect our product (v2) source, please check getProductSourceV2 API.', error)
        })
}

const setShoppingProductSource = data => {
    data.map(item => {
        item.stock = item.qty
        item.image = item.image_url
        item.video = item.video_url
        item.member_price = item.price
        item.qty = 0
        if (item.image === '') {
            item.image = 'https://member-calls.unicity.com/api/unishop/assets/false/JPN/none.jpg'
        }
    })

    defaultShopStore.productDataSource = data
    shopStore.productDataSource = data
    shopStore.productDataNew = data
}

export const useProductSource = () => {
    useEffect(() => {
        reaction(
            () => StoreAuth.isInitilized,
            (init, pInit, rAuth) => {
                if (init === true && (isNothing(pInit) || pInit === false)) {
                    if (Country.path) {
                        startProductAPI()

                        reaction(
                            () => storeProduct.isInitialized,
                            (init, _, reaction) => {
                                if (init) {
                                    if (StoreAuth.isAuthorized) {
                                        if (findString(window.location.pathname, 'payment-response')) {
                                            storeManager.isCartRestoredDone = true
                                        }
                                    }
                                    reaction.dispose()
                                }
                            }
                        )
                    }

                    rAuth.dispose()
                }
            },
            { fireImmediately: true }
        )
    }, [])
}

export const refreshProduct = () => {
    const status = StoreUser.getShortStatus()
    const allow = 'shop'
    const baId = StoreAuth.id

    Raven.getProductSourceV2({
        allow,
        status,
        warehouse: storeProduct.warehouseSelectedValue,
        ...(baId ? { baId } : {})
    })
        .then(response => {
            const legacyResponse = mappingToLegacyResponse(response)
            processProductSource(legacyResponse)
            logn('✅ 🐉 Successfully refresh product (v2) source from database.')
        })
        .then(() => StoreAuth.isAuthorized && removeNotExistsCartItems())
        .then(() => StoreAuth.isAuthorized && removeOutOfStockCartItems())
        .catch(error => {
            loge('❌ 🐉 Failed to connect our product (v2) source, please check getProductSourceV2 API.', error)
        })
}

export const setSelectedWarehouse = _setSelectedWarehouse
export const getSelectedWarehouse = _getSelectedWarehouse
